import { useEffect, useState } from "react";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, RefreshCw, ShieldCheck, ShieldX } from "lucide-react";
import { AiRiskPanel } from "@/components/t4r/AiRiskPanel";

const DecisionButton = ({ active, onClick, tone, icon: Icon, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${active ? (tone === "rose" ? "bg-rose-600 border-rose-600 text-white" : "bg-green-600 border-green-600 text-white") : "bg-white border-border text-slate-600 hover:bg-slate-50"}`}>
    <Icon size={11} /> {children}
  </button>
);

const WrittenMark = ({ row, mark, onChange, idx }) => {
  const ai = row.ai_detection;
  const confirmed = mark.ai_decision === "confirmed";
  return (
    <div className="rounded-lg border border-border p-3 space-y-2" data-testid={`mark-answer-${idx}`}>
      <p className="text-sm font-medium">{row.prompt}</p>
      <p className="whitespace-pre-wrap rounded bg-slate-50 p-2 text-xs">{row.text || <em className="text-muted-foreground">No answer given</em>}</p>
      <AiRiskPanel ai={ai} idx={idx} />
      <div className="flex flex-wrap gap-1.5">
        <DecisionButton active={confirmed} tone="rose" icon={ShieldX} onClick={() => onChange({ ai_decision: confirmed ? null : "confirmed", points: confirmed ? mark.points : 0 })} testId={`mark-confirm-ai-${idx}`}>Confirm AI use</DecisionButton>
        {ai?.is_flagged && <DecisionButton active={mark.ai_decision === "cleared"} icon={ShieldCheck} onClick={() => onChange({ ai_decision: mark.ai_decision === "cleared" ? null : "cleared" })} testId={`mark-clear-flag-${idx}`}>Clear flag</DecisionButton>}
      </div>
      <div className="grid grid-cols-[110px_1fr] gap-3">
        <div>
          <Label className="text-xs">Points (max {row.max_points || 1})</Label>
          <Input type="number" min="0" max={row.max_points || 1} step="0.5" disabled={confirmed} value={mark.points ?? ""} onChange={(e) => onChange({ points: e.target.value === "" ? null : Number(e.target.value) })} data-testid={`mark-points-${idx}`} />
        </div>
        <div>
          <Label className="text-xs">Feedback for the learner</Label>
          <Textarea value={mark.feedback} onChange={(e) => onChange({ feedback: e.target.value })} className="min-h-[60px] text-xs" data-testid={`mark-feedback-${idx}`} />
        </div>
      </div>
    </div>
  );
};

export const MarkAttemptDialog = ({ attempt: initial, onClose, onSaved }) => {
  const [attempt, setAttempt] = useState(initial);
  const written = (attempt?.answers || []).filter((a) => a.kind === "written");
  const [marks, setMarks] = useState({});
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);

  useEffect(() => { setAttempt(initial); }, [initial]);

  const rescan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post(`/lms/admin/attempts/${attempt.id}/rescan`);
      setAttempt((a) => ({ ...a, answers: data.answers, ai_flags: data.ai_flags }));
      toast.success("AI scan re-run");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Re-scan failed"); }
    finally { setScanning(false); }
  };

  useEffect(() => {
    const init = {};
    (initial?.answers || []).filter((a) => a.kind === "written").forEach((r) => { init[r.question_id] = { points: r.awarded_points ?? null, feedback: r.feedback || "", ai_decision: r.ai_decision || null }; });
    setMarks(init);
  }, [initial]);

  if (!attempt) return null;
  const update = (qid, patch) => setMarks((m) => ({ ...m, [qid]: { ...m[qid], ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/lms/admin/attempts/${attempt.id}/mark`, { answers: Object.entries(marks).map(([question_id, m]) => ({ question_id, ...m })) });
      toast.success("Marking saved"); onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed to save marking"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!initial} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="mark-attempt-dialog">
        <DialogHeader>
          <DialogTitle>Mark — {attempt.learner_name}</DialogTitle>
          <DialogDescription className="text-xs">{attempt.lesson_title || "Lesson quiz"} · submitted {new Date(attempt.submitted_at).toLocaleString()}. Confirming AI use scores that answer 0. Leaving points empty keeps the attempt awaiting marking.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {written.map((r, i) => <WrittenMark key={r.question_id} row={r} idx={i} mark={marks[r.question_id] || { feedback: "" }} onChange={(p) => update(r.question_id, p)} />)}
          {written.length === 0 && <p className="text-sm text-muted-foreground italic">No written answers on this attempt.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" className="sm:mr-auto gap-1.5" disabled={scanning || written.length === 0} onClick={rescan} data-testid="mark-rescan">
            {scanning ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Re-run AI scan
          </Button>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-green-700 hover:bg-green-800 text-white" disabled={saving || written.length === 0} onClick={save} data-testid="mark-save">Save marking</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
